import { Fragment, useState, useEffect } from 'react'
import classNames from 'classnames'
import _ from '@strings'
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete'
import Text from '@components/Ui/Text'
import Icon from '@components/Ui/Icon'
import Loader from '@components/Ui/Loader'
import config from '@config'
import styles from './LocationAutoComplete.scss'

const LocationAutoComplete = ({ onSelect, disabled, preset }) => {
  const country = config.get('local.country')
  let countries = []

  if (country === 'ch') {
    countries = [country, 'li']
  } else {
    countries = country
  }

  const opts = {
    cache: 1 * 60 * 60,
    requestOptions: {
      language: config.get('local.locale'),
      componentRestrictions: {
        country: countries,
      },
      types: ['geocode'],
    },
    debounce: 300,
  }

  const { ready, value, suggestions, setValue, clearSuggestions } = usePlacesAutocomplete(opts)

  const [error, setError] = useState(null)
  const [active, setActive] = useState(false)
  const [focus, setFocus] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (preset) {
      setValue(preset, false)
      clearSuggestions()
    }

    requestGeolocation()
  }, [true])

  const requestGeolocation = () => {
    if (navigator.geolocation) {
      setLoading(true)
      navigator.geolocation.getCurrentPosition(
        (position) => {
          getGeocode({
            location: { lat: position.coords.latitude, lng: position.coords.longitude },
          })
            .then((results) => results?.[0] || false)
            .then((result) => {
              setValue(result.formatted_address)
              onSelect(position.coords.latitude, position.coords.longitude, result)
              setLoading(false)
            })
            .catch((error) => {
              setLoading(false)
              setError(_('location.error_get_location'))
            })
        },
        (err) => {
          console.log(err)
          setLoading(false)
        }
      )
    }
  }

  const clearField = () => {
    setValue('', false)
    clearSuggestions()
  }

  const handleSelect = (location) => {
    setValue(location.description, false)
    clearSuggestions()

    // Get latitude and longitude via utility functions
    getGeocode({ address: location.description })
      .then((results) => getLatLng(results[0]))
      .then((result) => {
        onSelect(result.lat, result.lng, location)
      })
      .catch((error) => {
        console.error('😱 Error: ', error)
      })
  }

  const handleMouseEnter = () => {
    setActive(true)
  }

  const handleMouseLeave = () => {
    if (!focus) setActive(false)
  }

  const handleInputFocus = () => {
    setFocus(true)
    setActive(true)
  }

  const handleInputBlur = () => {
    setFocus(false)
    setActive(false)
  }

  const hasResults = suggestions.status === 'OK'

  return (
    <Fragment>
      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.base} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <div className={styles.input}>
          <input
            type="text"
            placeholder={_('location.placeholder')}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={!ready || loading || disabled}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            data-e2e="appointments-location-input"
          />
        </div>

        <div className={styles.searchIcons}>
          <div
            className={classNames(styles.iconLocation, {
              [styles.active]: active,
            })}
          >
            {!loading && value && (
              <div onClick={clearField}>
                <Icon type="close" size="14" />
              </div>
            )}
            {!loading && !value && (
              <div onClick={requestGeolocation}>
                <Icon type="local" size="24" />
              </div>
            )}
            {loading && (
              <div className={styles.loader}>
                <Loader />
              </div>
            )}
          </div>
        </div>

        <div
          className={classNames(styles.dropdown, {
            [styles.active]: active,
          })}
        >
          {hasResults &&
            suggestions.data.map((s) => {
              const {
                place_id: placeId,
                structured_formatting: { main_text: title, secondary_text: subtext },
              } = s

              return (
                <button
                  type="button"
                  key={placeId}
                  onClick={() => handleSelect(s)}
                  className={styles.item}
                  data-e2e="appointments-location-item"
                >
                  <Text type="robo_md_bold">{title}</Text>
                  <Text type="robo_md">{`, ${subtext}`}</Text>
                </button>
              )
            })}
        </div>
      </div>
    </Fragment>
  )
}

LocationAutoComplete.defaultProps = {}
LocationAutoComplete.propTypes = {}

export default LocationAutoComplete
